import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCmsImagePath } from '../cms/cmsData';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import SponsorsSection from '../components/SponsorsSection';
import ScrollToTop from '../components/ScrollToTop';
import TornPaperWrapper from '../components/TornPaperWrapper';
import { getArabicTranslation } from '../utils/arabicTranslations';
import { useCms } from '../cms/CmsContext';
import { buildLineupDaySections, getArtistLineup, getFestivalSummary, getLineupSummary } from '../data/artistLineup';
import './TicketsFunnel.css';
import './MusicEntertainment.css';

const LineupSummary = ({ rows, note }) => (
  <div className="funnel-lineup-summary" role="list" aria-label="Artist lineup by day">
    {rows.map((row) => (
      <div key={row.day} className="funnel-lineup-row" role="listitem">
        <span className="funnel-lineup-day">{row.day}:</span>{' '}
        <span className="funnel-lineup-artists">{row.artists}</span>
      </div>
    ))}
    {note ? <p className="funnel-lineup-note">{note}</p> : null}
  </div>
);

const PerformerGrid = ({ performers, ticketPath, compact }) => (
  <div className={`music-ent-grid ${compact ? 'music-ent-grid-2' : ''}`}>
    {performers.map((performer, index) => (
      <Link
        key={`${performer.name}-${index}`}
        to={ticketPath}
        className="music-ent-card-link"
        title={performer.name}
      >
        <img
          loading="lazy"
          src={performer.image}
          alt={performer.name}
          className="music-ent-card-image"
        />
      </Link>
    ))}
  </div>
);

const StageSection = ({ name, ticketPath, buttonText, performers, hasPacAmp }) => (
  <div className="music-ent-stage-block">
    <h3 className="music-ent-stage-title">
      {name}
      <span className="arabic-text on-white">{getArabicTranslation(name)}</span>
    </h3>
    <p className="music-ent-stage-note">
      {hasPacAmp ? 'Festival or PAC AMP ticket required.' : 'Festival ticket required.'} Purchase festival tickets{' '}
      <Link to={ticketPath} className="music-ent-inline-link" title={name}>
        here
      </Link>{' '}
      (<span className="music-ent-price-amount">starting at $20</span>)
    </p>
    <PerformerGrid performers={performers} ticketPath={ticketPath} compact={performers.length === 2} />
    <div className="music-ent-stage-btn-wrap">
      <Link to={ticketPath} className="music-ent-stage-btn">
        {buttonText}
      </Link>
    </div>
  </div>
);

const Tickets = () => {
  const { content } = useCms();
  const artistLineup = getArtistLineup(content);
  const daySections = buildLineupDaySections(artistLineup);
  const festivalLineup = getFestivalSummary(artistLineup);
  const ampLineup = getLineupSummary(artistLineup, 'pac-amp');

  useEffect(() => {
    document.title = 'OC MENA Festival & Concert Tickets';
  }, []);

  return (
    <div className="tickets-funnel-wrapper">
      <AnnouncementBar />
      
      <section className="tickets-funnel-hero">
        <div className="hero-background-wrapper">
          <video src={"/hero-final.mp4"} alt="oc mena festival" className="hero-background-video" autoPlay muted loop playsInline />
          <div className="hero-gradient-overlay"></div>
        </div>

        <TornPaperWrapper>
          <h1 className="funnel-heading">
            OC MENA Festival & Concert Tickets
            <span className="arabic-text on-white">{getArabicTranslation('Tickets')}</span>
          </h1>

          <div className="funnel-cards">
            {/* Festival Tickets Card */}
            <div className="funnel-card">
              <span className="funnel-option-badge">Option 1</span>
              <h2 className="funnel-card-title">
                Festival Tickets 🎉
                <span className="arabic-text on-white arabic-below">{getArabicTranslation('Festival Tickets')}</span>
              </h2>
              <LineupSummary rows={festivalLineup} />

              <div className="funnel-card-image">
                <img loading="lazy" src={getCmsImagePath('festivalTicketsCard', '/festival2.webp')} alt="OC MENA Festival" />
              </div>

              <p className="funnel-card-description">
                Choose from single-day or full-weekend passes to experience what the festival has to offer. Each ticket grants access to the festival grounds, booths, food, and other fun activities.
              </p>

              <div className="funnel-notice">
                <span className="funnel-notice-icon">i</span>
                <span className="funnel-notice-text">
                  <strong>Festival tickets do not include access to the amphitheater.</strong>
                </span>
              </div>

              <Link to="/festival-tickets" className="funnel-cta-btn" title="View Festival Tickets">
                View Festival Tickets
              </Link>
            </div>

            {/* Concert / Amphitheater Tickets Card */}
            <div className="funnel-card">
              <span className="funnel-option-badge">Option 2</span>
              <h2 className="funnel-card-title">
                Pacific Amphitheatre Tickets 🎤
                <span className="arabic-text on-white">{getArabicTranslation('Pacific Amphitheatre Tickets')}</span>
              </h2>
              <LineupSummary
                rows={ampLineup}
                note={<span className="funnel-lineup-note-emphasis">*Includes access to festival and indoor stage performances in Option 1</span>}
              />

              <div className="funnel-card-image">
                <img loading="lazy" src={getCmsImagePath('stadiumImage', '/stadium.webp')} alt="Pacific Amphitheatre" />
              </div>

              <p className="funnel-card-description">
                Amphitheater tickets are a premium, separately ticketed concert experience at the Pacific Amphitheatre with reserved seating. To view seating options, click on View Amphitheater Tickets below.
              </p>

              <div className="funnel-notice">
                <span className="funnel-notice-icon">i</span>
                <span className="funnel-notice-text">
                  Each Amphitheater ticket <strong>includes admission to the OC MENA Festival for the same day as the performance.</strong> Guests do not need to purchase a separate festival ticket for that day.
                </span>
              </div>

              <Link to="/amphitheater-tickets" className="funnel-cta-btn" title="View Amphitheater Tickets">
                View Amphitheater Tickets
              </Link>
            </div>
          </div>

          <section className="music-ent-container music-ent-compact" style={{ marginTop: '3rem' }}>
            {daySections.map((day) => (
              <article key={day.key} className="music-ent-day-section">
                <header className="music-ent-day-header">
                  <h2>
                    {day.dayLabel}
                    <span className={`arabic-text on-dark arabic-inline ${day.key === 'sunday' ? 'sunday-arabic' : ''}`}>{getArabicTranslation(day.dayLabel)}</span>
                  </h2>
                  <p>{day.dateLabel}</p>
                </header>

                <div className="music-ent-stage-block music-ent-pac-block">
                  {day.pacAmp.hasPerformances && (
                    <h3 className="music-ent-stage-title">
                      Pacific Amphitheatre (PAC AMP Stage)
                      <span className="arabic-text on-white">{getArabicTranslation('Pacific Amphitheatre')}</span>
                    </h3>
                  )}
                  {day.pacAmp.hasPerformances ? (
                    <>
                      <p className="music-ent-stage-note">
                        {day.pacAmp.notePrefix} Purchase tickets{' '}
                        <Link to={day.pacAmp.ticketPath} className="music-ent-inline-link" title={day.pacAmp.djName}>
                          here
                        </Link>{' '}
                        (<span className="music-ent-price-amount">starting at $32</span>)
                      </p>
                      <PerformerGrid performers={day.pacAmp.performers} ticketPath={day.pacAmp.ticketPath} />
                      {day.pacAmp.djName && <div className="music-ent-dj-row">
                        <span className="music-ent-dj-label">DJ Appearance by</span>
                        {day.pacAmp.djImage && <img
                          src={day.pacAmp.djImage}
                          alt={`DJ ${day.pacAmp.djName}`}
                          className="music-ent-dj-avatar"
                        />}
                        <span className="music-ent-dj-name">{day.pacAmp.djName}</span>
                      </div>}
                      <div className="music-ent-stage-btn-wrap">
                        <Link to={day.pacAmp.ticketPath} className="music-ent-stage-btn">
                          {day.pacAmp.buttonText}
                        </Link>
                      </div>
                    </>
                  ) : (
                    <div className="music-ent-no-pac">{day.pacAmp.noPerformanceText}</div>
                  )}
                </div>

                {day.stages.map((stage) => (
                  <StageSection
                    key={`${day.key}-${stage.name}`}
                    name={stage.name}
                    ticketPath={stage.ticketPath}
                    buttonText={stage.buttonText}
                    performers={stage.performers}
                    hasPacAmp={day.pacAmp.hasPerformances}
                  />
                ))}
              </article>
            ))}
          </section>

          <div className="funnel-support-images" style={{ marginTop: '2.5rem' }}>
            <div className="funnel-support-image-frame">
              <img
                loading="lazy"
                src="/ocmena-official-flyer(1080x1350)-c.png"
                alt="OC MENA Festival Official Flyer"
                className="funnel-support-image"
              />
            </div>

            <div className="funnel-support-image-frame">
              <img
                loading="lazy"
                src="/how_tickets_work.jpeg"
                alt="How Tickets Work"
                className="funnel-support-image"
              />
            </div>

            <div className="funnel-support-image-frame">
              <img
                loading="lazy"
                src="/map.png"
                alt="OC MENA Festival Event Map"
                className="funnel-support-image"
              />
            </div>
          </div>
        </TornPaperWrapper>

        <div className="lanterns-container">
          <img loading="lazy" src={getCmsImagePath('lanterns', '/lanterns.webp')} alt="festival lanterns" className="lanterns-image" />
        </div>
      </section>

      <SponsorsSection />
      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default Tickets;
